import { useState } from 'react';
import { useActiveLocation } from '@/hooks/useActiveLocation';
import { useAppDispatch } from '@/store';
import { pushToast } from '@/store/uiSlice';
import {
  useGetLocationQuery,
  useGetRestaurantsQuery,
  useUpdateLocationMutation,
  useUpdateRestaurantMutation
} from '@/store/configApi';
import { Button, Field, Panel, SkeletonRows } from '@/components/ui';

/**
 * Business information, editable after setup.
 *
 * The onboarding wizard collects these once; restaurants move, change their
 * phone number and adjust prep times, so every field it captured has to remain
 * changeable without anyone touching the database.
 */
export function BusinessPage() {
  const dispatch = useAppDispatch();
  const { locationId } = useActiveLocation();
  const { data: location, isLoading } = useGetLocationQuery(locationId, { skip: !locationId });
  const { data: restaurants } = useGetRestaurantsQuery();
  const [updateLocation, { isLoading: savingLocation }] = useUpdateLocationMutation();
  const [updateRestaurant, { isLoading: savingRestaurant }] = useUpdateRestaurantMutation();

  const restaurant = restaurants?.data?.[0];

  const [locationDraft, setLocationDraft] = useState<Record<string, unknown>>({});
  const [restaurantDraft, setRestaurantDraft] = useState<Record<string, unknown>>({});

  const loc = { ...(location ?? {}), ...locationDraft } as Record<string, string | number | boolean>;
  const rest = { ...(restaurant ?? {}), ...restaurantDraft } as Record<string, string>;

  const setLoc = (key: string, next: unknown) =>
    setLocationDraft((prev) => ({ ...prev, [key]: next }));
  const setRest = (key: string, next: unknown) =>
    setRestaurantDraft((prev) => ({ ...prev, [key]: next }));

  const dirty = Object.keys(locationDraft).length > 0 || Object.keys(restaurantDraft).length > 0;

  const save = async () => {
    const results = await Promise.all([
      Object.keys(locationDraft).length > 0
        ? updateLocation({ id: locationId, ...locationDraft })
        : Promise.resolve({}),
      Object.keys(restaurantDraft).length > 0 && restaurant
        ? updateRestaurant({ id: restaurant._id, ...restaurantDraft })
        : Promise.resolve({})
    ]);

    if (results.some((result) => 'error' in result)) {
      dispatch(pushToast('Could not save your business information.', 'error'));
      return;
    }

    dispatch(pushToast('Business information saved.', 'success'));
    setLocationDraft({});
    setRestaurantDraft({});
  };

  if (isLoading) return <SkeletonRows rows={8} />;

  return (
    <div className="space-y-4">
      <h1 className="text-display">Business information</h1>

      <Panel title="Restaurant">
        <div className="grid gap-4 px-4 py-4 sm:grid-cols-2">
          <Field label="Name">
            <input
              className="input"
              value={rest.name ?? ''}
              onChange={(event) => setRest('name', event.target.value)}
            />
          </Field>
          <Field label="Cuisine">
            <input
              className="input"
              value={rest.cuisine ?? ''}
              onChange={(event) => setRest('cuisine', event.target.value)}
            />
          </Field>
          <Field label="Main phone">
            <input
              className="input tabular"
              value={rest.mainPhone ?? ''}
              onChange={(event) => setRest('mainPhone', event.target.value)}
            />
          </Field>
          <Field label="Website">
            <input
              className="input"
              value={rest.website ?? ''}
              onChange={(event) => setRest('website', event.target.value)}
            />
          </Field>
        </div>
      </Panel>

      <Panel title="Location">
        <div className="grid gap-4 px-4 py-4 sm:grid-cols-2">
          <Field label="Location name">
            <input
              className="input"
              value={String(loc.name ?? '')}
              onChange={(event) => setLoc('name', event.target.value)}
            />
          </Field>
          <Field label="Timezone">
            <input
              className="input"
              value={String(loc.timezone ?? '')}
              onChange={(event) => setLoc('timezone', event.target.value)}
            />
          </Field>
          <Field label="Address">
            <input
              className="input"
              value={String(loc.addressLine1 ?? '')}
              onChange={(event) => setLoc('addressLine1', event.target.value)}
            />
          </Field>
          <Field label="Suite or unit">
            <input
              className="input"
              value={String(loc.addressLine2 ?? '')}
              onChange={(event) => setLoc('addressLine2', event.target.value)}
            />
          </Field>
          <Field label="City">
            <input
              className="input"
              value={String(loc.city ?? '')}
              onChange={(event) => setLoc('city', event.target.value)}
            />
          </Field>
          <Field label="Province or state">
            <input
              className="input"
              value={String(loc.region ?? '')}
              onChange={(event) => setLoc('region', event.target.value)}
            />
          </Field>
          <Field label="Postal code">
            <input
              className="input"
              value={String(loc.postalCode ?? '')}
              onChange={(event) => setLoc('postalCode', event.target.value)}
            />
          </Field>
          <Field label="Country">
            <input
              className="input"
              value={String(loc.country ?? '')}
              onChange={(event) => setLoc('country', event.target.value)}
            />
          </Field>
        </div>
      </Panel>

      <Panel title="What the line can tell callers">
        <div className="space-y-4 px-4 py-4">
          <p className="text-small text-slate">
            The phone line answers only from what you write here. Anything left blank simply will
            not be offered to a caller.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Type of business">
              <select
                className="input"
                value={String(loc.vertical ?? 'RESTAURANT')}
                onChange={(event) => setLoc('vertical', event.target.value)}
              >
                <option value="RESTAURANT">Restaurant (menu, orders, kitchen)</option>
                <option value="RETAIL">Retail / supplements (products, orders)</option>
                <option value="GYM">Gym (products, memberships)</option>
                <option value="CLINIC">Clinic (services, requests)</option>
                <option value="SALON">Salon (services, bookings)</option>
              </select>
            </Field>
            <Field label="Average prep time (minutes)">
              <input
                type="number"
                min="0"
                className="input tabular"
                value={Number(loc.averagePrepTimeMinutes ?? 20)}
                onChange={(event) => setLoc('averagePrepTimeMinutes', Number(event.target.value))}
              />
            </Field>
            <Field label="Parking">
              <input
                className="input"
                value={String(loc.parkingInfo ?? '')}
                onChange={(event) => setLoc('parkingInfo', event.target.value)}
              />
            </Field>
            <Field label="Pickup instructions">
              <input
                className="input"
                value={String(loc.pickupInfo ?? '')}
                onChange={(event) => setLoc('pickupInfo', event.target.value)}
              />
            </Field>
            <Field label="Delivery information">
              <input
                className="input"
                value={String(loc.deliveryInfo ?? '')}
                onChange={(event) => setLoc('deliveryInfo', event.target.value)}
              />
            </Field>
          </div>

          <label className="flex items-start gap-2.5">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={Boolean(loc.orderingEnabled)}
              onChange={(event) => setLoc('orderingEnabled', event.target.checked)}
            />
            <span>
              <span className="block text-small text-ink">Take orders over the phone</span>
              <span className="block text-micro text-slate">
                Off means the line answers questions and passes ordering calls to your staff.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2.5">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={Boolean(loc.recordingEnabled)}
              onChange={(event) => setLoc('recordingEnabled', event.target.checked)}
            />
            <span>
              <span className="block text-small text-ink">Record calls</span>
              <span className="block text-micro text-slate">
                Recordings appear on the call detail page. Check your local rules on notifying
                callers before turning this on.
              </span>
            </span>
          </label>
        </div>
      </Panel>

      <div className="flex justify-end">
        <Button onClick={save} loading={savingLocation || savingRestaurant} disabled={!dirty}>
          Save changes
        </Button>
      </div>
    </div>
  );
}
